import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import { Loader } from '@aws-amplify/ui-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { InstancePicker } from '../components/InstancePicker';
import { ValidatedLaunchDialog } from '../components/ValidatedLaunchDialog';
import { api, ApiError, type AppSummary } from '../lib/api';
import { requireAppStream, type AppStreamEmbedInstance } from '../lib/appstream';
import { useMe } from '../lib/use-me';
import {
  canSwitchView,
  defaultView,
  isInternalOrg,
  readStoredView,
  storeView,
  type InstanceView,
} from '../lib/instance-view';
import { isValidatedInstance } from '../lib/validated';
import {
  readStoredGrouping,
  storeGrouping,
  type Grouping,
} from '../lib/grouping';
import '../styles/page.scss';
import '../styles/x5client.scss';

type Phase = 'loading' | 'list' | 'launching' | 'streaming';

export function X5Client() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [apps, setApps] = useState<AppSummary[]>([]);
  const [active, setActive] = useState<AppSummary | null>(null);
  const [needsProvisioning, setNeedsProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingValidated, setPendingValidated] = useState<AppSummary | null>(null);
  const me = useMe();

  // Only SafetyNet's own organization gets the validated treatment. A customer
  // launching their own system does not need to be warned about it.
  const flagValidated = isInternalOrg(me);

  // Null until the user picks one, so an account whose instance count or
  // organization changes still follows the derived default rather than a
  // preference it never expressed.
  const [chosenView, setChosenView] = useState<InstanceView | null>(() => readStoredView());

  // Alphabetical unless SafetyNet has asked for customers, and remembered
  // either way. Unlike the view there is nothing to derive it from, so an
  // absent preference is simply 'alpha'.
  const [grouping, setGrouping] = useState<Grouping>(() => readStoredGrouping() ?? 'alpha');

  // A ref, not a local. The version this replaces held the embed in a plain
  // `let` inside the component body, so every re-render dropped the reference
  // and teardown silently did nothing — leaving an orphaned session iframe
  // behind on unmount.
  const embedRef = useRef<AppStreamEmbedInstance | null>(null);

  const destroyEmbed = useCallback(() => {
    const embed = embedRef.current;
    if (!embed) return;
    embedRef.current = null;
    try {
      embed.endSession();
      embed.destroy();
    } catch {
      // Already torn down by the SDK; nothing left to do.
    }
  }, []);

  const closeSession = useCallback(() => {
    destroyEmbed();
    setActive(null);
    setPhase('list');
  }, [destroyEmbed]);

  // Every state write happens after the first await. `phase` already starts as
  // 'loading', so the spinner needs no synchronous set on mount — which is what
  // keeps this out of react-hooks/set-state-in-effect without suppressing it.
  const loadApplications = useCallback(async () => {
    try {
      const { items } = await api.listApplications();
      setApps(items);
      setNeedsProvisioning(false);
      setError(null);
    } catch (err) {
      // Signed in but not yet attached to an organization. Its own panel, not an
      // error banner — the customer needs to be told who to contact.
      if (err instanceof ApiError && err.isNotProvisioned) {
        setNeedsProvisioning(true);
        setApps([]);
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load your applications.');
      }
    } finally {
      setPhase('list');
    }
  }, []);

  useEffect(() => {
    // The application list has to be fetched on mount — it is the entire page,
    // and there is nothing to render until it arrives. Every state write in
    // `loadApplications` happens after its first await, so this is not the
    // cascading-render case the rule targets; the rule cannot see that across
    // the useCallback boundary. Same call and same reasoning as x5-admin's
    // AuthProvider bootstrap.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadApplications();
    return () => destroyEmbed();
  }, [loadApplications, destroyEmbed]);

  const changeView = useCallback((next: InstanceView) => {
    setChosenView(next);
    storeView(next);
  }, []);

  const changeGrouping = useCallback((next: Grouping) => {
    setGrouping(next);
    storeGrouping(next);
  }, []);

  const launch = useCallback(
    async (app: AppSummary) => {
      setActive(app);
      setPhase('launching');
      setError(null);
      try {
        const sdk = requireAppStream();
        const session = await api.createSession(app.id);

        destroyEmbed();
        setPhase('streaming');

        // The container only exists once `phase` is 'streaming', so the embed has
        // to be constructed after React has painted that frame.
        requestAnimationFrame(() => {
          try {
            const embed = new sdk.Embed('x5as2-container', {
              sessionURL: session.embedUrl,
              userInterfaceConfig: {
                [sdk.Embed.Options.HIDDEN_ELEMENTS]: [
                  sdk.Embed.Elements.CATALOG_BUTTON,
                  sdk.Embed.Elements.WINDOW_SWITCHER_BUTTON,
                  sdk.Embed.Elements.SETTINGS_BUTTON,
                ],
              },
            });

            embed.addEventListener(sdk.Embed.Events.SESSION_STATE_CHANGE, (event) => {
              const status = String(event[sdk.Embed.EventParams.STATUS] ?? '');
              const resolved = sdk.Embed.SessionStatus[status];
              if (
                resolved === sdk.Embed.SessionStatus.Disconnected ||
                resolved === sdk.Embed.SessionStatus.Ended
              ) {
                closeSession();
              }
            });
            embed.addEventListener(sdk.Embed.Events.SESSION_ERROR, () => {
              setError('The X5 session ended unexpectedly. Try launching again.');
              closeSession();
            });

            embedRef.current = embed;
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not start the X5 viewer.');
            setActive(null);
            setPhase('list');
          }
        });
      } catch (err) {
        // The original swallowed this path into a TypeError — it called the
        // state value `isAppselected(false)` instead of its setter — so a failed
        // launch left the UI stuck on "Launching X5....".
        setError(err instanceof Error ? err.message : 'Could not start your X5 session.');
        setActive(null);
        setPhase('list');
      }
    },
    [closeSession, destroyEmbed],
  );

  // Validated systems are a customer's controlled environment, so opening one
  // is confirmed rather than one click away. Everything else launches directly.
  const requestLaunch = useCallback(
    (app: AppSummary) => {
      if (flagValidated && isValidatedInstance(app.displayName)) setPendingValidated(app);
      else void launch(app);
    },
    [flagValidated, launch],
  );

  const confirmPending = useCallback(() => {
    setPendingValidated(null);
    if (pendingValidated) void launch(pendingValidated);
  }, [launch, pendingValidated]);

  // The header stays mounted through the session, so the account menu and the
  // way out of a stream are always reachable. The embed then has to size to
  // whatever is left rather than to a hard-coded viewport offset, which is what
  // the flex column below the header does.
  if (phase === 'streaming') {
    return (
      <div className="x5-page x5-page--stream">
        <Header>
          <div className="x5-session">
            <span className="x5-session__name" title={active?.displayName}>
              {active?.displayName}
            </span>
            <button type="button" className="x5-session__close" onClick={closeSession}>
              End session
            </button>
          </div>
        </Header>
        <div id="x5as2-container" className="x5as2-container" />
      </div>
    );
  }

  const switchable = canSwitchView(me, apps.length);

  return (
    <div className="x5-page">
      <Header />
      <main className="x5-page__body">
        {error && (
          <Alert
            variant="danger"
            className="x5-page__alert"
            onClose={() => setError(null)}
            dismissible
          >
            {error}
          </Alert>
        )}

        {phase === 'launching' ? (
          <section className="x5-launching">
            <h1 className="x5-launching__title">Launching {active?.displayName ?? 'X5'}</h1>
            <p className="x5-launching__hint">This takes a few seconds.</p>
            <Loader variation="linear" />
          </section>
        ) : phase === 'loading' ? (
          <div className="x5-page__loading">
            <Loader variation="linear" />
          </div>
        ) : apps.length > 0 ? (
          <InstancePicker
            apps={apps}
            // A stored 'dense' must not follow a user into an account too small
            // to offer the switch, or they get list chrome they cannot turn off.
            view={switchable ? (chosenView ?? defaultView(me, apps.length)) : 'cozy'}
            grouping={flagValidated ? grouping : 'alpha'}
            showViewSwitch={switchable}
            showGrouping={flagValidated}
            flagValidated={flagValidated}
            onViewChange={changeView}
            onGroupingChange={changeGrouping}
            onLaunch={requestLaunch}
          />
        ) : (
          needsProvisioning && <AccountSetupNotice />
        )}
      </main>
      <Footer />

      {pendingValidated && (
        <ValidatedLaunchDialog
          app={pendingValidated}
          onCancel={() => setPendingValidated(null)}
          onConfirm={confirmPending}
        />
      )}
    </div>
  );
}

function AccountSetupNotice() {
  return (
    <Alert variant="light" className="x5-setup">
      <Alert.Heading>Account Setup</Alert.Heading>
      <p>
        Please reach out to the{' '}
        <Alert.Link href="https://safetynet.xiltrixusa.com/hc/en-us/requests/new">
          SafetyNet
        </Alert.Link>{' '}
        Team to finish provisioning your account.
      </p>
      <p>
        Send an email to{' '}
        <Alert.Link href="mailto:safetynet@xiltrixusa.com">SafetyNet@xiltrixusa.com</Alert.Link> or
        submit a ticket on our{' '}
        <Alert.Link href="https://safetynet.xiltrixusa.com/hc/en-us/requests/new">
          online portal
        </Alert.Link>{' '}
        and request access to your X5 Server.
      </p>
    </Alert>
  );
}
